import { useState, type CSSProperties } from 'react'
import type { PostValue } from '../../api/client'
import type { AutonomyPage, Row } from '../../api/settingsAutonomy'
import { n, post, s, useMsg } from './AutonomyPage'

/**
 * Sectiunea "Alarm response" - `settings/autonomy/response.php` +
 * `response-2.js`: matricea pe severitati (Notify / Ticket / AI / Escalate /
 * Save-inherit), suprascrierile pe tip de alarma, randul de adaugare si
 * "What the response did lately". Fiecare rand isi trimite propriile campuri
 * la `saveResponse` cu scope-ul panoului; "inherit" / "remove" la
 * `deleteResponse`; dupa OK, ecranul se reincarca.
 */
const AI_LABELS: [string, string][] = [
  ['off', 'off - people only'],
  ['diagnose', 'diagnose (read-only, posts findings)'],
  ['propose', 'propose a fix (a person approves)'],
  ['remediate', 'fix it (incident-safe actions, then verify)'],
  ['desk', 'service desk (proactive run; needs proactive_too on)'],
]
const SEV_TONE: Record<string, string> = { '3': '#b42318', '2': '#b54708', '1': '#1f5f8b', '0': '#6b7480' }
const STATE_TONE: Record<string, string> = { open: '#6b7480', ai_pending: '#1f5f8b', ai_done: '#1f5f8b', recovered: '#2e7d32', escalated: '#b42318', caused: '#8a6d3b' }
const small: CSSProperties = { fontSize: 11, height: 24, padding: '2px 6px' }

type R = AutonomyPage['response']

function ids(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : []
}

function fmt(d: string): string {
  const t = new Date(d.replace(' ', 'T'))
  if (Number.isNaN(t.getTime())) return d
  return `${String(t.getDate()).padStart(2, '0')} ${t.toLocaleString('en-GB', { month: 'short' })} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`
}

function People({ people, value, onChange, height, title }: { people: Row[]; value: string[]; onChange(v: string[]): void; height: number; title?: string }) {
  if (people.length === 0) return null
  return (
    <select multiple className="form-control" style={{ fontSize: 11, height, marginTop: 4 }} title={title} value={value} onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}>
      {people.map((p) => {
        const id = String(n(p.id_user))
        const name = s(p.name).trim() || `user #${id}`
        return <option key={id} value={id}>{name}</option>
      })}
    </select>
  )
}

/** Un rand al matricei / al suprascrierilor / cel de adaugare: aceleasi campuri, acelasi POST. */
function PolicyRow({ r, p, severity, type, isNew, onChange, children }: { r: R; p: Row; severity: number; type: number; isNew?: boolean; onChange(): void; children?: (fields: () => Record<string, PostValue>, msg: React.ReactNode, say: (t: string, tone: 'wait' | 'ok' | 'bad') => void) => React.ReactNode }) {
  const [msg, say] = useMsg()
  const [f, setF] = useState({
    notify: isNew ? true : Boolean(p.notify), notify_company: Boolean(p.notify_company), notify_users: ids(p.notify_users),
    ticket: Boolean(p.ticket), ticket_after_minutes: String(n(p.ticket_after_minutes)), ticket_resolve: isNew ? true : Boolean(p.ticket_resolve),
    ai: s(p.ai) || 'off', escalate_after_minutes: String(n(p.escalate_after_minutes)), escalate_users: ids(p.escalate_users),
    newType: '0', newSev: '-1',
  })
  const set = (patch: Partial<typeof f>) => setF({ ...f, ...patch })
  const fields = (): Record<string, PostValue> => ({
    scope: r.scope, alert_type: String(isNew ? Number(f.newType) : type), severity: String(isNew ? Number(f.newSev) : severity),
    notify: f.notify ? '1' : '0', notify_company: f.notify_company ? '1' : '0', notify_users: f.notify_users,
    ticket: f.ticket ? '1' : '0', ticket_after_minutes: String(parseInt(f.ticket_after_minutes, 10) || 0), ticket_resolve: f.ticket_resolve ? '1' : '0',
    ai: f.ai, escalate_after_minutes: String(parseInt(f.escalate_after_minutes, 10) || 0), escalate_users: f.escalate_users,
  })
  const save = () => {
    if (isNew && Number(f.newType) < 1) { say('Pick an alarm type first', 'bad'); return }
    post('/response', fields(), say, () => setTimeout(onChange, 600))
  }
  const remove = () => post('/response/delete', { scope: r.scope, id: String(n(p.ID)) }, say, () => setTimeout(onChange, 600), 'Removed')

  if (isNew) {
    return (
      <div className="odar-row" id="odar-new" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
        <select className="form-control odar-new-type" style={{ width: 240, ...small, height: 26 }} value={f.newType} onChange={(e) => set({ newType: e.target.value })}>
          <option value="0">- alarm type -</option>
          {r.types.map((t) => <option key={n(t.ID)} value={String(n(t.ID))}>{s(t.type_name)}</option>)}
        </select>
        <select className="form-control odar-new-sev" style={{ width: 130, ...small, height: 26 }} value={f.newSev} onChange={(e) => set({ newSev: e.target.value })}>
          <option value="-1">any severity</option>
          {Object.entries(r.severities).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
        </select>
        <label style={{ margin: 0, fontWeight: 400 }}><input type="checkbox" className="odar-notify" checked={f.notify} onChange={(e) => set({ notify: e.target.checked })} /> notify</label>
        <label style={{ margin: 0, fontWeight: 400 }}><input type="checkbox" className="odar-notify-company" checked={f.notify_company} onChange={(e) => set({ notify_company: e.target.checked })} /> + company users</label>
        <label style={{ margin: 0, fontWeight: 400 }}><input type="checkbox" className="odar-ticket" checked={f.ticket} onChange={(e) => set({ ticket: e.target.checked })} /> ticket</label>
        after <input type="number" min={0} max={1440} className="form-control odar-after" style={{ width: 60, display: 'inline-block', ...small, padding: '1px 4px' }} value={f.ticket_after_minutes} onChange={(e) => set({ ticket_after_minutes: e.target.value })} /> min
        <label style={{ margin: 0, fontWeight: 400 }}><input type="checkbox" className="odar-resolve" checked={f.ticket_resolve} onChange={(e) => set({ ticket_resolve: e.target.checked })} /> auto-resolve</label>
        <select className="form-control odar-ai" style={{ width: 230, ...small, height: 26 }} value={f.ai} onChange={(e) => set({ ai: e.target.value })}>
          {AI_LABELS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
        </select>
        escalate after <input type="number" min={0} max={10080} className="form-control odar-escalate-after" style={{ width: 60, display: 'inline-block', ...small, padding: '1px 4px' }} value={f.escalate_after_minutes} onChange={(e) => set({ escalate_after_minutes: e.target.value })} /> min
        <button type="button" className="btn btn-default btn-xs odar-save" onClick={save}>Add override</button>
        {msg}
      </div>
    )
  }

  const inherited = Boolean(p.inherited)
  const isMatrix = type === 0
  return (
    <tr className="odar-row">
      <td style={isMatrix ? undefined : { width: 220 }}>
        {isMatrix ? (
          <>
            <strong style={{ color: SEV_TONE[String(severity)] }}>{s(p.label)}</strong>
            {inherited ? <div className="text-muted" style={{ fontSize: 11 }}>{r.scope === 'sp' ? 'factory default' : 'inherited'}</div> : <div style={{ fontSize: 11, color: '#2e7d32' }}>set here</div>}
          </>
        ) : (
          <>
            <strong>{s(p.type_name)}</strong>
            <div className="text-muted" style={{ fontSize: 11 }}>{severity >= 0 ? r.severities[String(severity)] : 'any severity'}</div>
          </>
        )}
      </td>
      <td>
        <label style={{ margin: 0, fontWeight: 400, display: isMatrix ? 'block' : undefined }}><input type="checkbox" className="odar-notify" checked={f.notify} onChange={(e) => set({ notify: e.target.checked })} /> {isMatrix ? 'bell for the people who respond' : 'notify'}</label>
        {!isMatrix && <>&nbsp; </>}
        <label style={{ margin: 0, fontWeight: 400, display: isMatrix ? 'block' : undefined }}><input type="checkbox" className="odar-notify-company" checked={f.notify_company} onChange={(e) => set({ notify_company: e.target.checked })} /> {isMatrix ? "also the company's own users" : '+ company users'}</label>
        <People people={r.people} value={f.notify_users} onChange={(v) => set({ notify_users: v })} height={isMatrix ? 54 : 40} title="people to notify besides whoever is on call today" />
        {isMatrix && r.people.length > 0 && <div className="text-muted" style={{ fontSize: 10 }}>on-call today always hears; pick more here (Ctrl/Cmd-click). Nobody on call and nobody picked = the provider admins.</div>}
      </td>
      <td>
        <label style={{ margin: 0, fontWeight: 400, display: isMatrix ? 'block' : undefined }}><input type="checkbox" className="odar-ticket" checked={f.ticket} onChange={(e) => set({ ticket: e.target.checked })} /> {isMatrix ? 'open an Easeedesk ticket' : 'ticket'}</label>
        {isMatrix ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
            <span>after</span>
            <input type="number" min={0} max={1440} className="form-control odar-after" style={{ width: 70, ...small }} value={f.ticket_after_minutes} onChange={(e) => set({ ticket_after_minutes: e.target.value })} />
            <span>min (0 = at once; otherwise only if still open)</span>
          </div>
        ) : (
          <> after <input type="number" min={0} max={1440} className="form-control odar-after" style={{ width: 60, display: 'inline-block', ...small, height: 22, padding: '1px 4px' }} value={f.ticket_after_minutes} onChange={(e) => set({ ticket_after_minutes: e.target.value })} /> min &nbsp; </>
        )}
        <label style={{ margin: isMatrix ? '4px 0 0' : 0, fontWeight: 400, display: isMatrix ? 'block' : undefined }}><input type="checkbox" className="odar-resolve" checked={f.ticket_resolve} onChange={(e) => set({ ticket_resolve: e.target.checked })} /> {isMatrix ? 'resolve the ticket when the alarm closes' : 'auto-resolve'}</label>
      </td>
      <td style={isMatrix ? undefined : { width: 230 }}>
        <select className="form-control odar-ai" style={{ ...small, height: isMatrix ? 26 : 24 }} value={f.ai} onChange={(e) => set({ ai: e.target.value })}>
          {AI_LABELS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
        </select>
        {isMatrix && <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>&quot;fix it&quot; needs the Autonomy switch ON and the daily cap; otherwise it proposes. One machine, one run per half hour. Findings go into the ticket.</div>}
      </td>
      <td style={isMatrix ? undefined : { width: 200 }}>
        {isMatrix ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span>after</span>
            <input type="number" min={0} max={10080} className="form-control odar-escalate-after" style={{ width: 70, ...small }} value={f.escalate_after_minutes} onChange={(e) => set({ escalate_after_minutes: e.target.value })} />
            <span>min untouched</span>
          </div>
        ) : (
          <>escalate after <input type="number" min={0} max={10080} className="form-control odar-escalate-after" style={{ width: 60, display: 'inline-block', ...small, height: 22, padding: '1px 4px' }} value={f.escalate_after_minutes} onChange={(e) => set({ escalate_after_minutes: e.target.value })} /> min</>
        )}
        <People people={r.people} value={f.escalate_users} onChange={(v) => set({ escalate_users: v })} height={isMatrix ? 54 : 40} title="who hears the escalation; the first one becomes the ticket assignee" />
        {isMatrix && <div className="text-muted" style={{ fontSize: 10, marginTop: 4 }}>0 = never. Otherwise: still open after that long, ticket not assigned / commented / moved and bell not dismissed = red bell to these people (first one gets the ticket); nobody picked = the provider admins.</div>}
      </td>
      <td style={{ width: 150 }}>
        <button type="button" className="btn btn-default btn-xs odar-save" onClick={save}>Save</button>{' '}
        {isMatrix ? (
          !inherited && <button type="button" className="btn btn-link btn-xs odar-delete" title="drop this row; the row above (provider default or factory) applies again" onClick={remove}>inherit</button>
        ) : (
          <button type="button" className="btn btn-link btn-xs odar-delete" onClick={remove}>remove</button>
        )}
        <div style={{ fontSize: 11, marginTop: 3 }}>{msg}</div>
      </td>
      {children?.(fields, msg, say)}
    </tr>
  )
}

export function AutonomyResponsePanel({ r, isGlobal, companyId, companyName, onChange }: { r: R; isGlobal: boolean; companyId: number; companyName: string; onChange(): void }) {
  const today = r.today
  return (
    <div className="panel panel-default" id="odar" data-scope={r.scope}>
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Alarm response</strong>
          <span className="text-muted" style={{ fontSize: 12 }}>
            what happens the minute an alarm opens: who is told in the portal, whether a ticket opens (now, or only if the alarm is still there after N minutes), whether the AI agent takes it - and the ticket resolving itself when the alarm closes. Per severity, with overrides per alarm type. Consequences are recognised: a guest whose host is silent, or a probe on a machine that is itself silent, is filed under the cause&apos;s ticket instead of ringing again.
          </span>
        </div>
        {!r.ready && (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
            The table is created by the cron on its first run; until then the defaults apply (critical: notify + ticket + AI diagnose; major: notify + ticket after 15 min; minor: notify; info: nothing).
          </div>
        )}
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
          {isGlobal ? (
            <span style={{ fontSize: 12 }}>
              Editing: <a href="/app/settings/autonomy" style={{ fontWeight: r.scope === 'company' ? 600 : undefined }}>this company ({companyName || `#${companyId}`})</a> &middot;{' '}
              <a href="/app/settings/autonomy?scope=sp" style={{ fontWeight: r.scope === 'sp' ? 600 : undefined }}>provider default (all companies)</a>
            </span>
          ) : (
            <span style={{ fontSize: 12 }}>Editing: <strong>{companyName || `#${companyId}`}</strong></span>
          )}
          {n(today.responses) > 0 && (
            <span className="text-muted" style={{ fontSize: 12 }}>
              today: {n(today.responses)} alarm(s) answered, {n(today.tickets)} ticket(s), AI on {n(today.ai)}, fixed and verified {n(today.fixed)}, escalated {n(today.escalated)}
              {n(today.caused) > 0 && <>, {n(today.caused)} filed as consequences (silent host or silent machine - no bell, no ticket of their own)</>}
            </span>
          )}
        </div>
        <table className="table table-condensed" style={{ marginTop: 10, fontSize: 12 }}>
          <thead><tr><th style={{ width: 90 }}>Severity</th><th>Notify in portal</th><th>Ticket</th><th>AI agent</th><th>Escalate</th><th style={{ width: 150 }}></th></tr></thead>
          <tbody>
            {Object.entries(r.matrix).map(([sev, p]) => <PolicyRow key={`${r.scope}-${sev}-${n(p.ID)}`} r={r} p={p} severity={Number(sev)} type={0} onChange={onChange} />)}
          </tbody>
        </table>
        <div style={{ borderTop: '1px solid #eef0f2', paddingTop: 10, marginTop: 4 }}>
          <strong style={{ fontSize: 13 }}>Per alarm type</strong>{' '}
          <span className="text-muted" style={{ fontSize: 12 }}>an override beats the severity row above - e.g. &quot;Agent last seen&quot; never opens a ticket, &quot;Disk usage&quot; on critical is fixed by the agent. Without any row, the forecast alarms (disk filling up, SMART sectors growing, unusual load) are diagnosed by the AI even at Major or Minor - a forecast is only worth having if someone explains what is growing before it hurts; diagnosis is read-only.</span>
          {r.overrides.length > 0 && (
            <table className="table table-condensed" style={{ marginTop: 8, fontSize: 12 }}>
              <tbody>
                {r.overrides.map((o) => <PolicyRow key={n(o.ID)} r={r} p={o} severity={n(o.severity)} type={n(o.alert_type)} onChange={onChange} />)}
              </tbody>
            </table>
          )}
          <PolicyRow r={r} p={{}} severity={-1} type={0} isNew onChange={onChange} />
        </div>
        {r.recent.length > 0 && (
          <div style={{ borderTop: '1px solid #eef0f2', paddingTop: 10, marginTop: 10 }}>
            <strong style={{ fontSize: 13 }}>What the response did lately</strong>
            <table className="table table-condensed" style={{ marginTop: 6, fontSize: 12 }}>
              <tbody>
                {r.recent.map((x, i) => {
                  let label = s(x.type_name).trim() || `type #${n(x.alert_type)}`
                  if (s(x.hardware_identifier).trim()) label += ` ${s(x.hardware_identifier).trim()}`
                  const state = s(x.state)
                  return (
                    <tr key={i}>
                      <td style={{ width: 120, whiteSpace: 'nowrap' }} className="text-muted">{fmt(s(x.created))}</td>
                      <td>
                        <strong style={{ color: SEV_TONE[String(n(x.severity))] ?? '#6b7480' }}>{r.severities[String(n(x.severity))] ?? 'Info'}</strong> {label} <span className="text-muted">on</span>{' '}
                        <a href={`/app/proactivitee/deviceAlarms/${n(x.device_id)}`}>{s(x.device)}</a>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}><span style={{ padding: '1px 8px', borderRadius: 9, fontSize: 11, fontWeight: 600, color: '#fff', background: STATE_TONE[state] ?? '#6b7480' }}>{state.replace(/_/g, ' ')}</span></td>
                      <td>
                        {n(x.notified) > 0 && <><span className="text-muted">told {n(x.notified)}</span> &middot; </>}
                        {n(x.ticket_id) > 0 && <><a href={`/app/easeedesk/ticket/${n(x.ticket_id)}`}>ticket #{n(x.ticket_id)}</a>{n(x.ticket_shared) ? ' (shared)' : ''} &middot; </>}
                        {s(x.ai_mode) !== 'off' && s(x.ai_mode) !== '' && <>AI {s(x.ai_mode)}{s(x.ai_outcome) !== '' ? <>: <strong>{s(x.ai_outcome).replace(/_/g, ' ')}</strong></> : ' (queued)'}{s(x.ai_summary).trim() && <div className="text-muted" style={{ fontSize: 11 }}>{s(x.ai_summary)}</div>}</>}
                        {s(x.note).trim() && <div className="text-muted" style={{ fontSize: 11 }}>{s(x.note)}</div>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
